import { useEffect, useState } from 'react';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import {
  AppVersionConfigSchema,
  parseBuildNumber,
  type AppVersionConfig,
} from '@etiquetachef/types';
import { APP_VERSION } from './appVersion';
import { firestore } from './firebase';

const TAG = 'admin/version-gate';

/** Veredito do kill switch de versão para o build em execução. */
export type AppVersionGateState =
  | { kind: 'OK' }
  | { kind: 'UPDATE_AVAILABLE'; latestVersion: string }
  | { kind: 'FORCE_UPDATE'; latestVersion: string };

// Lê `/appConfig/admin` em tempo real e decide se o build atual está OK,
// com atualização disponível (banner) ou abaixo do mínimo suportado
// (kill switch — tela bloqueante). Falhas de rede ou doc inexistente
// caem em OK por desenho — gate é defensivo, não pode trancar o
// super-admin por bug nosso ou config ainda não publicada.
//
// Doc PRÓPRIO do admin (não `/appConfig/web`): o build do admin é
// independente do app cliente, então comparar contra o latestBuild do
// web forçaria update a toda hora. O release.yml publica `/appConfig/admin`
// com minBuild = latestBuild a cada deploy do admin (CDN propaga
// instantâneo), espelhando a política web de force-update.
//
// Usa onSnapshot para reagir em tempo real: deploy novo aperta o
// latestBuild e a aba aberta vê o gate sem reload manual.
/**
 * Kill switch de versão do admin: lê `/appConfig/admin` em tempo real.
 *
 * Origem: sem issue — commit 17dc7b267, 2026-06-19 (version gate com force
 * update no admin).
 *
 * Contrato: devolve `OK`, `UPDATE_AVAILABLE` ou `FORCE_UPDATE`. Doc ausente,
 * inválido ou erro de snapshot → `OK` (o gate nunca tranca o super-admin por
 * bug nosso). No-op em dev e em build sem `+BUILD` na versão.
 *
 * Usado por: `components/AppVersionGate.tsx` (tela bloqueante/banner) e
 * `components/NavVersionFooter.tsx` (link "Atualizar").
 *
 * Arquitetura: escrito pelo `release.yml` (step "Publish appConfig/admin")
 * com `minBuild = latestBuild` a cada deploy; leitura pela rule
 * `match /appConfig/{platform}` de `firebase/firestore.rules`.
 *
 * Dados — `/appConfig/admin` (schema `AppVersionConfigSchema`, em
 * `@etiquetachef/types`):
 *   { "minBuild": 218, "latestBuild": 218, "latestVersion": "2026.10.09+218",
 *     "updatedAt": 1760000000000 }   // updatedAt opcional, epoch ms
 *
 * Falhas conhecidas:
 *  - Doc inválido e erro de snapshot vão só para o `console.warn`, sem report
 *    na telemetria — um `appConfig/admin` quebrado desligaria o kill switch
 *    sem ninguém ver (#926, achado da frente de docstrings).
 *
 * Dicas: o desvio em `import.meta.env.DEV` evita o loop de tela bloqueante no
 * dev server (versão congelada pelo `define` do Vite) — não remover.
 */
export function useAppVersionGate(): AppVersionGateState {
  const [state, setState] = useState<AppVersionGateState>({ kind: 'OK' });

  useEffect(() => {
    if (import.meta.env.DEV) {
      // Dev local: o `define` do Vite congela a versão (`__APP_VERSION__`)
      // no boot do dev server. Um bump no `package.json` + `minBuild` novo
      // em `/appConfig/admin` dispararia FORCE_UPDATE até reiniciar o Vite,
      // e "Atualizar agora" só faz `reload()` (não relê o `define`) → loop
      // de tela bloqueante. O kill switch só faz sentido em prod, onde o
      // reload puxa o `index.html` novo do CDN. Em dev é no-op.
      return;
    }

    const currentBuild = parseBuildNumber(APP_VERSION);
    if (currentBuild === 0) {
      // Dev local (`pkg.version` sem `+BUILD`) — nunca dispara gate.
      return;
    }

    const ref = doc(firestore, 'appConfig', 'admin');
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // Doc ainda não publicado pelo CI — comportamento permissivo.
          setState({ kind: 'OK' });
          return;
        }
        const parsed = AppVersionConfigSchema.safeParse(snap.data());
        if (!parsed.success) {
          console.warn(`${TAG}: appConfig/admin inválido`, parsed.error.issues);
          setState({ kind: 'OK' });
          return;
        }
        setState(decideGate(currentBuild, parsed.data));
      },
      (err) => {
        // Rede/permissão/etc. Não trancamos — apenas logamos.
        console.warn(`${TAG}: snapshot error`, err);
        setState({ kind: 'OK' });
      },
    );
    return unsub;
  }, []);

  return state;
}

/** Abaixo do `minBuild` → FORCE_UPDATE; abaixo do `latestBuild` → UPDATE_AVAILABLE; senão OK. */
function decideGate(
  currentBuild: number,
  config: AppVersionConfig,
): AppVersionGateState {
  if (currentBuild < config.minBuild) {
    return { kind: 'FORCE_UPDATE', latestVersion: config.latestVersion };
  }
  if (currentBuild < config.latestBuild) {
    return { kind: 'UPDATE_AVAILABLE', latestVersion: config.latestVersion };
  }
  return { kind: 'OK' };
}
